import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { GET_TAGS } from '../../lib/graphql/queries/contacts';

const PAGE_SIZE = 50;

interface GQLTag {
  tagId: string;
  tagName: string;
  contactCount: number;
  isSystemTag: boolean;
  createdAt: string;
}

interface GetTagsData {
  tags: {
    tags: GQLTag[];
    total: number;
    hasMore: boolean;
  };
}

export const useUniqueContactTags = (enabled: boolean = false) => {
  const { organization } = useOrganizationStore();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');

  const { data, loading, error, fetchMore } = useQuery<GetTagsData>(GET_TAGS, {
    variables: {
      organizationId: organization?.id ?? '',
      limit: PAGE_SIZE,
      offset: 0,
      sortBy: 'tagName',
      sortOrder: 'ASC',
      ...(search ? { search } : {}),
    },
    skip: !enabled || !organization?.id,
  });

  const tags = data?.tags?.tags ?? [];
  const total = data?.tags?.total ?? 0;
  const hasMore = data?.tags?.hasMore ?? false;

  const tagNames = useMemo(() => tags.map((t) => t.tagName), [tags]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMore({
      variables: { offset: nextPage * PAGE_SIZE },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          tags: {
            ...fetchMoreResult.tags,
            tags: [...(prev.tags?.tags ?? []), ...fetchMoreResult.tags.tags],
          },
        };
      },
    });
  }, [hasMore, loading, page, fetchMore]);

  const handleSearch = useCallback((query: string) => {
    setSearch(query);
    setPage(0);
  }, []);

  return {
    data: tagNames,
    tags,
    total,
    hasMore,
    isLoading: loading,
    isError: !!error,
    error: error ?? null,
    loadMore,
    search,
    setSearch: handleSearch,
  };
};
